import React, { useState, useEffect } from 'react';
import domain from '../../../../reusecomponent/domain';
import { getToken } from '../../../function/token';
import { ErrorToastContainer, SuccessToastContainer } from '../../../function/CustomFunction';

export default function FileModal({ open, onClose, node, onSave }) {
  const [formData, setFormData] = useState({
    sendMethod: 'upload', // 'upload' or 'custom'
    fileUrl: '',
    delay: 0,
    customField: ''
  });
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);

  useEffect(() => {
    if (open && node) {
      setFormData({
        sendMethod: node.data.sendMethod || 'upload',
        fileUrl: node.data.fileUrl || '',
        delay: node.data.delay || 0,
        customField: node.data.customField || ''
      });
    }
  }, [open, node]);

  const handleSave = () => {
    if (onSave) {
      onSave(node.id, formData);
    }
    onClose();
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  };

  const handleDragOut = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      uploadFile(file);
    }
  };

  // File upload function
  const uploadFile = async (file) => {
    if (!file) return;

    setUploading(true);
    try {
      const token = getToken();
      if (!token) {
        ErrorToastContainer('Authentication token not found');
        setUploading(false);
        return;
      }

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${domain()}/campaign/uplode-file`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.status) {
        // Update the form data with the uploaded file URL
        setFormData(prev => ({
          ...prev,
          fileUrl: result.data || result.url || result.link || '',
          sendMethod: 'upload'
        }));
        setUploadedFile(file);
        SuccessToastContainer('File uploaded successfully!');
      } else {
        ErrorToastContainer(result.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      ErrorToastContainer('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Handle file selection from input
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      uploadFile(file);
    }
  };

  if (!open) return null;

  const modalStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  };

  const contentStyle = {
    background: '#ffffff',
    borderRadius: '8px',
    padding: '24px',
    minWidth: '600px',
    maxWidth: '700px',
    maxHeight: '80vh',
    overflowY: 'auto',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
  };

  const inputStyle = {
    width: '100%',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    padding: '8px 12px',
    fontSize: '14px',
    marginBottom: '12px',
    outline: 'none'
  };

  const buttonStyle = {
    background: '#007bff',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer',
    marginRight: '8px'
  };

  const secondaryButtonStyle = {
    background: '#6c757d',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer'
  };

  const infoBoxStyle = {
    background: '#e3f2fd',
    border: '1px solid #bbdefb',
    borderRadius: '6px',
    padding: '16px',
    marginBottom: '20px'
  };

  const uploadAreaStyle = {
    border: '2px dashed #d1d5db',
    borderRadius: '8px',
    padding: '40px 20px',
    textAlign: 'center',
    background: dragActive ? '#f8f9fa' : '#ffffff',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginBottom: '16px'
  };

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={contentStyle} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: '#495057', fontSize: '20px', fontWeight: '600' }}>
            Configure File
          </h2>
        </div>

        {/* Choose How to Send Media Section */}
        <div style={infoBoxStyle}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600', color: '#1976d2' }}>
            Choose How to Send Media
          </h3>
          <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#495057' }}>
            You can deliver media to users in two ways:
          </p>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontWeight: '600', color: '#1976d2', marginBottom: '4px' }}>
              Use Custom Field
            </div>
            <div style={{ fontSize: '13px', color: '#666' }}>
              Select this option if the file URL is already stored in a custom field. This is useful for dynamic or previously uploaded content.
            </div>
          </div>
          <div>
            <div style={{ fontWeight: '600', color: '#1976d2', marginBottom: '4px' }}>
              Upload New Media
            </div>
            <div style={{ fontSize: '13px', color: '#666' }}>
              Upload a new file directly from your device. This media will be sent with the message immediately upon upload.
            </div>
          </div>
        </div>

        {/* Send Method Selection */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <button
              style={{
                ...buttonStyle,
                background: formData.sendMethod === 'custom' ? '#007bff' : '#f8f9fa',
                color: formData.sendMethod === 'custom' ? '#ffffff' : '#495057',
                border: '1px solid #d1d5db'
              }}
              onClick={() => setFormData(prev => ({ ...prev, sendMethod: 'custom' }))}
            >
              Use Custom Field
            </button>
            <button
              style={{
                ...buttonStyle,
                background: formData.sendMethod === 'upload' ? '#007bff' : '#f8f9fa',
                color: formData.sendMethod === 'upload' ? '#ffffff' : '#495057',
                border: '1px solid #d1d5db'
              }}
              onClick={() => setFormData(prev => ({ ...prev, sendMethod: 'upload' }))}
            >
              Upload New Media
            </button>
          </div>
        </div>

        {/* Upload Area or Custom Field Input */}
        {formData.sendMethod === 'upload' ? (
          <div
            style={uploadAreaStyle}
            onDragEnter={handleDragIn}
            onDragLeave={handleDragOut}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-upload').click()}
          >
            <div style={{ fontSize: '48px', color: '#007bff', marginBottom: '12px' }}>
              📄
            </div>
            <div style={{ fontSize: '16px', color: '#495057', marginBottom: '8px' }}>
              Click to upload or drag and drop
            </div>
            <div style={{ fontSize: '13px', color: '#6c757d' }}>
              Supported media types: doc, docx, pdf, txt, ppt, pptx, xls, xlsx
            </div>
            <input
              id="file-upload"
              type="file"
              accept=".doc,.docx,.pdf,.txt,.ppt,.pptx,.xls,.xlsx"
              style={{ display: 'none' }}
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  console.log('File selected:', e.target.files[0]);
                }
              }}
            />
          </div>
        ) : (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#495057' }}>
              Custom Field
            </label>
            <select
              value={formData.customField}
              onChange={e => setFormData(prev => ({ ...prev, customField: e.target.value }))}
              style={inputStyle}
            >
              <option value="">Select custom field</option>
              <option value="file_url">File URL</option>
              <option value="document">Document</option>
              <option value="report">Report</option>
            </select>
          </div>
        )}

        {/* Custom Dropdown */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <span style={{ fontSize: '14px', color: '#495057' }}>Custom</span>
            <span style={{ fontSize: '16px' }}>⚙️</span>
            <span style={{ fontSize: '12px' }}>▼</span>
          </div>
          <input
            type="text"
            value={formData.fileUrl}
            onChange={e => setFormData(prev => ({ ...prev, fileUrl: e.target.value }))}
            placeholder="You can either upload a file or use a custom field."
            style={inputStyle}
          />
        </div>

        {/* Delay Setting */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#495057' }}>
            Delay in reply - {formData.delay} sec
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="range"
              min="0"
              max="30"
              value={formData.delay}
              onChange={e => setFormData(prev => ({ ...prev, delay: parseInt(e.target.value) }))}
              style={{ flex: 1 }}
            />
            <span style={{ fontSize: '14px', color: '#6c757d', minWidth: '50px' }}>
              {formData.delay} sec
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <button
            onClick={handleSave}
            style={{ ...buttonStyle, display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            💾 Save
          </button>
          <button
            onClick={onClose}
            style={{ ...secondaryButtonStyle, display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            ✕ Close
          </button>
        </div>
      </div>
    </div>
  );
} 